import { Stack, Typography, useTheme } from "@mui/material";
import { Tooltip } from "components";
import type { OverviewCardTone } from "../../types";

interface OverviewCardProps {
  label: string;
  value: number | string;
  /** Picks the tint / text colour pair. Defaults to the neutral grey card. */
  tone?: OverviewCardTone;
  /**
   * Explains a number that ignores the dashboard's resource filter, so it doesn't
   * read as a card that failed to update.
   */
  scopeNote?: string;
  onClick?: () => void;
}

export const OverviewCard = ({
  label,
  value,
  tone = "default",
  scopeNote,
  onClick,
}: OverviewCardProps) => {
  const theme = useTheme();

  const toneStyles = {
    critical: {
      background: theme.palette.primitive.system.red[25],
      hoverBackground: theme.palette.primitive.system.red[50],
      hoverBorder: theme.palette.primitive.system.red[100],
      label: theme.palette.primitive.system.red[800],
      value: theme.palette.primitive.system.red[800],
    },
    warning: {
      background: theme.palette.primitive.system.orange[25],
      hoverBackground: theme.palette.primitive.system.orange[50],
      hoverBorder: theme.palette.primitive.system.orange[100],
      label: theme.palette.primitive.system.orange[800],
      value: theme.palette.primitive.system.orange[800],
    },
    default: {
      background: theme.palette.primitive.neutrals.neutral[25],
      hoverBackground: theme.palette.primitive.neutrals.neutral[50],
      hoverBorder: theme.palette.primitive.neutrals.neutral[100],
      label: theme.palette.lightBg.low,
      value: theme.palette.lightBg.medium,
    },
  };

  const colors = toneStyles[tone];
  // A zero count has nothing to drill into, so the card stays inert.
  const isEmpty = value === 0 || value === "0";
  const isClickable = !!onClick && !isEmpty;

  const card = (
    <Stack
      component={isClickable ? "button" : "div"}
      type={isClickable ? "button" : undefined}
      onClick={isClickable ? onClick : undefined}
      aria-label={scopeNote ? `${label}. ${scopeNote}` : undefined}
      sx={{
        flex: 1,
        minWidth: 0,
        gap: "8px",
        p: "12px",
        border: "1px solid transparent",
        textAlign: "left",
        borderRadius: "8px",
        backgroundColor: colors.background,
        cursor: isClickable ? "pointer" : "default",
        transition: "filter 0.2s",
        ...(isClickable && {
          ":hover": {
            backgroundColor: colors.hoverBackground,
            border: `1px solid ${colors.hoverBorder}`,
          },
        }),
      }}
    >
      <Typography variant="caption-3" sx={{ color: colors.label }} noWrap>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ color: colors.value }}>
        {value}
      </Typography>
    </Stack>
  );

  if (!scopeNote) return card;

  return (
    <Tooltip title={scopeNote} disableSentenceCase>
      {card}
    </Tooltip>
  );
};
